import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, Cell } from "recharts";
import { Trophy, Target, Timer, Bookmark, ArrowUUpLeft, Play, Trash, FolderOpen, Share, EnvelopeSimple, PencilSimple, Flame } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SetDialog from "@/components/SetDialog";
import { catStyle } from "@/lib/categoryColors";
import { toast } from "sonner";

export default function Dashboard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [sets, setSets] = useState([]);
    const [editing, setEditing] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [streak, setStreak] = useState(null);
    const [celebrate, setCelebrate] = useState(false);
    const [chartDomain, setChartDomain] = useState("__all__");
    const [domainReport, setDomainReport] = useState(null);

    useEffect(() => {
        if (chartDomain === "__all__") { setDomainReport(null); return; }
        api.get(`/user/domain-report?domain=${encodeURIComponent(chartDomain)}`)
            .then((r) => setDomainReport(r.data))
            .catch(() => setDomainReport(null));
    }, [chartDomain]);

    const loadSets = () => api.get("/sets").then((r) => setSets(r.data.items || [])).catch(() => {});

    const loadStreak = () => api.get("/user/streak").then((r) => {
        const s = r.data;
        setStreak((prev) => {
            // Celebrate if today's warmup was just completed (previous state existed but wasn't done, now is)
            if (prev && !prev.done_today && s.done_today) {
                setCelebrate(true);
                setTimeout(() => setCelebrate(false), 3500);
            }
            return s;
        });
    }).catch(() => {});

    useEffect(() => {
        api.get("/attempts/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/attempts").then((r) => setAttempts(r.data.items || [])).catch(() => {});
        loadSets();
        loadStreak();
        // Poll streak every 20s so a fresh session updates the card
        const t = setInterval(loadStreak, 20000);
        return () => clearInterval(t);
    }, []);

    const retakeWrong = async (a) => {
        try {
            const { data } = await api.post("/attempts/retake-wrong", { attempt_id: a.id });
            if (!data.items || data.items.length === 0) {
                toast.success("No incorrect answers to retake — nice work!");
                return;
            }
            nav("/practice", {
                state: {
                    questions: data.items,
                    label: `${data.items.length} incorrect from ${new Date(a.created_at).toLocaleDateString()}`,
                },
            });
        } catch {
            toast.error("Failed to load retake session");
        }
    };

    const launchSet = async (s) => {
        try {
            const { data } = await api.get(`/sets/${s.id}/questions`);
            if (!data.items || data.items.length === 0) { toast.error("Set is empty"); return; }
            nav("/practice", { state: { questions: data.items, label: `Study set — ${s.name}` } });
        } catch {
            toast.error("Failed to load set");
        }
    };

    const deleteSet = async (s) => {
        if (!confirm(`Delete study set "${s.name}"?`)) return;
        try {
            await api.delete(`/sets/${s.id}`);
            toast.success("Deleted");
            loadSets();
        } catch {
            toast.error("Failed");
        }
    };

    const shareSet = async (s) => {
        try {
            const { data } = await api.post(`/sets/${s.id}/share`);
            const url = `${window.location.origin}/shared/${data.share_token}`;
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Share link copied");
            } catch {
                toast.success(`Share link: ${url}`);
            }
            loadSets();
        } catch {
            toast.error("Failed to create share link");
        }
    };

    const editSet = (s) => {
        setEditing(s);
        setDialogOpen(true);
    };

    const submitEdit = async (payload) => {
        if (!editing) return;
        try {
            await api.patch(`/sets/${editing.id}`, payload);
            toast.success("Updated");
            loadSets();
        } catch {
            toast.error("Failed to update");
        }
    };

    const useStreakFreeze = async () => {
        try {
            await api.post("/user/streak/freeze");
            toast.success("Yesterday frozen — streak safe!");
            loadStreak();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Freeze failed");
        }
    };

    const previewWeekly = () => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/user/weekly-report/preview`, "_blank", "noopener");
    };

    const scoreHistory = useMemo(() => {
        if (chartDomain === "__all__") {
            return (stats?.history || []).map((h, i) => ({ ...h, idx: i + 1 }));
        }
        // Filter attempts by domain and compute history from the raw attempts list
        const filtered = attempts
            .filter((a) => (a.domain || "All Domains") === chartDomain)
            .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
            .map((a, i) => ({ idx: i + 1, score_pct: a.score_pct, mode: a.mode, date: a.created_at }));
        return filtered;
    }, [stats, attempts, chartDomain]);

    const historyLineColor = DOMAIN_HEX[chartDomain] || "hsl(46 96% 52%)";

    return (
        <div className="mx-auto max-w-7xl px-6 py-12">
            <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
                <div>
                    <div className="overline mb-2 text-primary">Dashboard</div>
                    <h1 className="font-display text-4xl font-semibold tracking-tight">Welcome, {user?.name || user?.email}</h1>
                    <p className="mt-2 text-muted-foreground">Track your CRMA readiness at a glance.</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/practice" data-testid="dash-practice-cta"><Button className="bg-primary text-primary-foreground hover:bg-primary/90">Practice now</Button></Link>
                    <Link to="/exam" data-testid="dash-exam-cta"><Button variant="outline">Exam mode</Button></Link>
                    <Link to="/bookmarks" data-testid="dash-bookmarks-cta"><Button variant="ghost"><Bookmark size={16} className="mr-1" /> Bookmarks</Button></Link>
                    <Button variant="ghost" onClick={previewWeekly} data-testid="dash-weekly-preview"><EnvelopeSimple size={16} className="mr-1" /> Preview Friday recap</Button>
                </div>
            </div>

            {!user?.premium && (
                <div className="mb-8 border border-primary/30 bg-primary/5 p-6" data-testid="upgrade-banner">
                    <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                        <div>
                            <div className="overline text-primary">Upgrade</div>
                            <h3 className="mt-2 font-display text-xl font-semibold">You&apos;re on the free tier</h3>
                            <p className="text-sm text-muted-foreground">Unlock all 20+ questions, exam simulator, and analytics.</p>
                        </div>
                        <Link to="/pricing" data-testid="upgrade-cta"><Button className="bg-primary text-primary-foreground hover:bg-primary/90">See plans</Button></Link>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                <MetricCard icon={Trophy} label="Attempts" value={stats?.total_attempts ?? "—"} testid="metric-attempts" />
                <MetricCard icon={Target} label="Avg. Score" value={stats ? `${stats.avg_score_pct}%` : "—"} testid="metric-avg" />
                <MetricCard icon={Timer} label="Questions Answered" value={stats?.total_questions ?? "—"} testid="metric-questions" />
                <StreakCard streak={streak} celebrate={celebrate} onFreeze={useStreakFreeze} />
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="border border-border bg-card p-6">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="font-display text-lg font-semibold">Score history</h3>
                        <Select value={chartDomain} onValueChange={setChartDomain}>
                            <SelectTrigger className="h-8 w-56 text-xs" data-testid="history-domain"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All domains</SelectItem>
                                <SelectItem value="Internal Audit Roles and Responsibilities">Roles</SelectItem>
                                <SelectItem value="Risk Management Governance">Governance</SelectItem>
                                <SelectItem value="Risk Management Assurance">Assurance</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="mt-4 h-64">
                        <ResponsiveContainer>
                            <LineChart data={scoreHistory}>
                                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                                <XAxis dataKey="idx" fontSize={12} />
                                <YAxis fontSize={12} domain={[0, 100]} />
                                <Tooltip />
                                <Line type="monotone" dataKey="score_pct" stroke={historyLineColor} strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    {chartDomain !== "__all__" && scoreHistory.length === 0 && (
                        <div className="mt-4 text-center text-xs text-muted-foreground" data-testid="history-empty">
                            No attempts recorded for this domain yet.
                        </div>
                    )}
                    {chartDomain !== "__all__" && domainReport?.top_wrong?.length > 0 && (
                        <div className="mt-6 border-t border-border pt-6" data-testid="domain-deepdive">
                            <div className="overline mb-3 text-primary">Top wrong · {catStyle(chartDomain).short}</div>
                            <ul className="space-y-3">
                                {domainReport.top_wrong.map((q) => (
                                    <li key={q.id} className="flex items-start gap-3 text-sm" data-testid={`top-wrong-${q.id}`}>
                                        <span className="mt-0.5 inline-flex h-5 min-w-[24px] items-center justify-center rounded-sm bg-rose-100 px-1.5 text-[10px] font-semibold text-rose-900 dark:bg-rose-950/40 dark:text-rose-200">×{q.wrong_count}</span>
                                        <span className="line-clamp-2 text-foreground">{q.question}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="border border-border bg-card p-6">
                    <h3 className="font-display text-lg font-semibold">Domain performance</h3>
                    <div className="mt-4 h-64">
                        <ResponsiveContainer>
                            <BarChart data={(stats?.domain_stats || []).map((d) => ({ full: d.domain, name: catStyle(d.domain).short || d.domain.split(" ").slice(0, 2).join(" "), pct: d.pct, fill: DOMAIN_HEX[d.domain] || "#6b7280" }))}>
                                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                                <XAxis dataKey="name" fontSize={11} />
                                <YAxis domain={[0, 100]} fontSize={12} />
                                <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.full || ""} formatter={(v) => [`${v}%`, "Accuracy"]} />
                                <Bar dataKey="pct" name="Accuracy %">
                                    {(stats?.domain_stats || []).map((d, i) => (
                                        <Cell key={i} fill={DOMAIN_HEX[d.domain] || "#6b7280"} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {Object.entries(DOMAIN_HEX).slice(0, 3).map(([d, hex]) => (
                            <span key={d} className="inline-flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full" style={{ background: hex }} />
                                {catStyle(d).short}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-10">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold flex items-center gap-2"><FolderOpen weight="duotone" className="text-primary" size={18} /> My study sets</h3>
                    <span className="text-xs text-muted-foreground">Saved from your sessions · launch anytime</span>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {sets.length === 0 && (
                        <div className="col-span-full border border-dashed border-border bg-card/60 p-6 text-sm text-muted-foreground" data-testid="sets-empty">
                            No study sets yet. Finish a practice session and choose &ldquo;Save wrong as set&rdquo; or &ldquo;Save entire session&rdquo;.
                        </div>
                    )}
                    {sets.map((s) => {
                        const cs = catStyle(s.category);
                        return (
                        <div key={s.id} className={`flex items-center justify-between border bg-card p-4 ${cs.accent} ${s.featured ? "border-primary" : "border-border"}`} data-testid={`set-${s.id}`}>
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-sm border border-border bg-secondary/60 text-xl">{s.emoji || "📚"}</div>
                                <div className="min-w-0">
                                    <div className="truncate font-medium">{s.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {s.count} questions · {new Date(s.created_at).toLocaleDateString()}
                                        {s.share_token && <span className="ml-1 text-primary">· shared</span>}
                                        {s.featured && <span className="ml-1 text-primary">· ⭐ featured</span>}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => editSet(s)}
                                        className={`mt-1 inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider transition-opacity hover:opacity-80 ${s.category ? cs.badge : "border-dashed border-border text-muted-foreground"}`}
                                        title="Click to change category"
                                        data-testid={`cat-chip-${s.id}`}
                                    >
                                        {s.category || "+ Add category"}
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline" onClick={() => launchSet(s)} data-testid={`launch-set-${s.id}`}>
                                    <Play size={12} className="mr-1" /> Start
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => editSet(s)} title="Rename / emoji / category" data-testid={`edit-set-${s.id}`}>
                                    <PencilSimple size={12} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => shareSet(s)} title="Copy share link" data-testid={`share-set-${s.id}`}>
                                    <Share size={12} className={s.share_token ? "text-primary" : ""} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => deleteSet(s)} data-testid={`del-set-${s.id}`}>
                                    <Trash size={12} className="text-destructive" />
                                </Button>
                            </div>
                        </div>
                    );})}
                </div>
            </div>

            <div className="mt-10">
                <h3 className="font-display text-lg font-semibold">Recent attempts</h3>
                <div className="mt-4 border border-border">
                    {attempts.length === 0 && <div className="p-6 text-sm text-muted-foreground">No attempts yet — start a practice session.</div>}
                    {attempts.map((a) => {
                        const wrongCount = (a.total || 0) - (a.correct || 0);
                        return (
                            <div key={a.id} className="flex items-center justify-between border-b border-border p-4 last:border-b-0" data-testid={`attempt-row-${a.id}`}>
                                <div>
                                    <div className="text-sm font-medium capitalize">{a.mode} · {a.domain || "All domains"}</div>
                                    <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <div className="font-display text-lg">{a.score_pct}%</div>
                                        <div className="text-xs text-muted-foreground">{a.correct}/{a.total}</div>
                                    </div>
                                    {wrongCount > 0 && (
                                        <Button size="sm" variant="outline" onClick={() => retakeWrong(a)} data-testid={`retake-${a.id}`}>
                                            <ArrowUUpLeft size={14} className="mr-1" /> Retake {wrongCount} wrong
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SetDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                initial={editing}
                title="Edit study set"
                onSubmit={submitEdit}
            />
        </div>
    );
}

function MetricCard({ icon: Icon, label, value, testid }) {
    return (
        <div className="border border-border bg-card p-6" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-primary/30 text-primary">
                    <Icon weight="duotone" size={20} />
                </div>
                <div className="overline text-muted-foreground">{label}</div>
            </div>
            <div className="mt-4 font-display text-3xl font-semibold">{value}</div>
        </div>
    );
}

const DOMAIN_HEX = {
    "Internal Audit Roles and Responsibilities": "#0ea5e9",
    "Risk Management Governance": "#059669",
    "Risk Management Assurance": "#f43f5e",
    "Mixed": "#f59e0b",
    "All Domains": "#eab308",
};

function StreakCard({ streak, celebrate, onFreeze }) {
    const days = streak?.current_streak ?? 0;
    const done = streak?.done_today;
    const questionsToday = streak?.questions_today ?? 0;
    const target = streak?.target ?? 10;
    const progress = Math.min(100, Math.round((questionsToday / target) * 100));

    return (
        <div className="relative overflow-hidden border border-border bg-card p-6" data-testid="metric-streak">
            <div className="flex items-center gap-3">
                <motion.div
                    animate={celebrate ? { rotate: [0, -12, 12, -8, 8, 0], scale: [1, 1.3, 1] } : { rotate: 0, scale: 1 }}
                    transition={{ duration: 0.9 }}
                    className={`flex h-9 w-9 items-center justify-center rounded-sm border ${done ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}
                >
                    <Flame weight={done ? "fill" : "duotone"} size={20} />
                </motion.div>
                <div className="overline text-muted-foreground">Warmup streak</div>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
                <motion.div
                    key={days}
                    initial={{ scale: 1 }}
                    animate={celebrate ? { scale: [1, 1.4, 1] } : { scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="font-display text-3xl font-semibold"
                    data-testid="streak-days"
                >
                    {days}
                </motion.div>
                <span className="text-sm text-muted-foreground">day{days === 1 ? "" : "s"}</span>
                {streak?.longest_streak > 0 && streak.longest_streak !== days && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="streak-longest">best: {streak.longest_streak}</span>
                )}
            </div>
            {!done && (
                <div className="mt-3">
                    <div className="text-xs text-muted-foreground" data-testid="streak-progress">
                        {questionsToday}/{target} for today
                    </div>
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                        <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            )}
            {done && !celebrate && (
                <div className="mt-3 text-xs text-primary" data-testid="streak-done">✅ Today&apos;s warmup done — keep the flame alive tomorrow.</div>
            )}
            {streak?.can_freeze_yesterday && streak?.freeze_available && (
                <button
                    type="button"
                    onClick={onFreeze}
                    className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-xs font-medium text-foreground hover:bg-primary/20"
                    data-testid="use-freeze"
                    title="You have one freeze token this month. Protect yesterday and keep your streak."
                >
                    ❄️ Use freeze token (1/month)
                </button>
            )}
            {streak?.used_this_month && (
                <div className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="freeze-used">Freeze token used this month</div>
            )}
            <AnimatePresence>
                {celebrate && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="pointer-events-none absolute inset-0 flex items-center justify-center"
                    >
                        <div className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground shadow-lg" data-testid="streak-celebration">
                            🔥 Streak +1!
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
